// src/forms/create/BulkProfileUsers.jsx
import React, { useMemo, useRef, useState } from 'react';
import { useBulkProfile,useDownloadBulkTemplate } from '../../hooks/users/useCreate';

const BulkProfileUsers = ({ onClose }) => {
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const downloadTemplate = useDownloadBulkTemplate();
  const { mutate, data, isLoading, isSuccess, isError, error, reset } = useBulkProfile();

  const failedRows = useMemo(() => (data?.rows || []).filter(r => !r.success), [data]);
  const succeededRows = useMemo(() => (data?.rows || []).filter(r => r.success), [data]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
  };

  const handleUpload = () => {
    if (!file) return;
    mutate(file);
  };

  const clear = () => {
    setFile(null);
    fileRef.current && (fileRef.current.value = '');
    reset();
  };

  return (
    <div className="w-full">
      {/* Instructions */}
      <div className="text-sm text-gray-600 space-y-2 mb-4">
        <p>Upload an Excel (*.xlsx) with columns: <strong>Login</strong>, <strong>TellerId</strong> (optional), <strong>RoleId</strong>, <strong>BranchId</strong>.</p>
        <button
          type="button"
          onClick={downloadTemplate}
          className="text-blue-600 underline"
        >
          Download template
        </button>
      </div>

      {/* File picker */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-4">
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={handleFileChange}
          className="block w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700"
        />
        <div className="flex gap-2">
          <button
            type="button"
            disabled={!file || isLoading}
            onClick={handleUpload}
            className={`px-4 py-2 rounded text-white ${(!file || isLoading) ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#0E86D4] hover:bg-[#0d78bd]'}`}
          >
            {isLoading ? 'Uploading…' : 'Upload'}
          </button>
          <button
            type="button"
            onClick={clear}
            className="px-4 py-2 rounded border bg-white text-gray-700 hover:bg-gray-50"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Status */}
      {isError && (
        <div className="p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm mb-3">
          {error?.response?.data?.message || error?.message || 'Upload failed'}
        </div>
      )}

      {isSuccess && (
        <div className="p-3 rounded border border-green-200 bg-green-50 text-green-700 text-sm mb-3">
          Processed <b>{data?.total ?? 0}</b> row(s).&nbsp;
          Succeeded: <b>{data?.succeeded ?? 0}</b>.&nbsp;
          Failed: <b>{data?.failed ?? 0}</b>.
        </div>
      )}

      {/* Results table */}
      {isSuccess && (
        <div className="space-y-6">
          {!!failedRows.length && (
            <div>
              <div className="font-medium mb-2">Failed rows</div>
              <div className="border rounded overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 text-gray-700">
                    <tr>
                      <th className="text-left p-2">Row</th>
                      <th className="text-left p-2">Login</th>
                      <th className="text-left p-2">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failedRows.map((r, i) => (
                      <tr key={`f-${i}`} className="border-t">
                        <td className="p-2">{r.rowNumber}</td>
                        <td className="p-2">{r.login}</td>
                        <td className="p-2 text-red-600">{r.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!!succeededRows.length && (
            <div>
              <div className="font-medium mb-2">Succeeded rows</div>
              <div className="border rounded overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 text-gray-700">
                    <tr>
                      <th className="text-left p-2">Row</th>
                      <th className="text-left p-2">Login</th>
                    </tr>
                  </thead>
                  <tbody>
                    {succeededRows.map((r, i) => (
                      <tr key={`s-${i}`} className="border-t">
                        <td className="p-2">{r.rowNumber}</td>
                        <td className="p-2">{r.login}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer actions */}
      <div className="mt-6 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded border bg-white text-gray-700 hover:bg-gray-50"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default BulkProfileUsers;
